import { useApi } from '@/api';
import { Circle, Link, TableSkeleton } from '@/components';
import { DATE_MAPPING } from '@/constants';
import { collateralColorPalette } from '@/theme/utils/collateralColorPalette';
import {
  Box,
  CryptoIcon,
  CurrencyValue,
  DataTable,
  Flex,
  PercentageValue,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useTheme } from 'styled-components';

export default function GroupsTable({ daysAgo }) {
  const { sorting } = useTableProps({
    initialSorting: '-debt',
    filtersDebounceTime: 50
  });
  const [data, error] = useApi({
    url: '/groups/',
    queryParams: {
      order: sorting.currentSorting,
      days_ago: daysAgo
    }
  });
  const theme = useTheme();

  if (error) {
    throw error;
  }

  const date = DATE_MAPPING[daysAgo];
  if (data) {
    const columns: TableColumn[] = [
      {
        header: '',
        getCell: (row) => (
          <Flex alignContent="center" alignItems="center" gap="2xs">
            <Circle
              size="1"
              color={collateralColorPalette(
                row.group_name,
                theme.colors.collateralColors
              )}
            />
            {row.group_name}
          </Flex>
        ),
        columnSize: ['minmax(100px, auto)', 'minmax(150px, auto)']
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Debt
            <CryptoIcon name="DAI" size="0" />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.debt} />,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'debt'
      },
      {
        header: `Change (${date})`,
        getCell: (row) => (
          <CurrencyValue
            value={row.debt_change}
            options={{
              trend: true,
              iconSize: '0'
            }}
          />
        ),
        getCellBottom: (row) => (
          <PercentageValue
            color="muted"
            fontSize="-1"
            value={row.debt_change_percentage}
            options={{
              nullable: (
                <Box color="muted" fontSize="-1">
                  -
                </Box>
              )
            }}
          />
        ),

        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'debt_change'
      },
      {
        header: 'Collateral',
        getCell: (row) => (
          <CurrencyValue
            value={row.collateral}
            options={{
              currency: 'USD'
            }}
          />
        ),
        getCellBottom: (row) => (
          <CurrencyValue
            fontSize="-1"
            value={row.collateral_change}
            options={{
              trend: true,
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'collateral'
      },
      {
        header: 'Collateral Ratio',
        getCell: (row) => <PercentageValue value={row.collateral_ratio} />,
        getCellBottom: (row) => (
          <PercentageValue
            fontSize="-1"
            value={row.collateral_ratio_change}
            options={{
              trend: true,
              iconSize: '-1',
              nullable: ''
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'collateral_ratio'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.group}
        rowLinkProps={{
          to: (row) =>
            row.group === 'seal'
              ? 'seal-engine'
              : row.group === 'staked'
                ? 'staking-engine'
                : row.group,
          // @ts-ignore
          linkComponent: Link
        }}
        sorting={sorting}
      />
    );
  }
  return <TableSkeleton rows={5} hasDoubleCell={true} />;
}
